"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { THEME_STORAGE_KEY, THEMED_ROUTE_PREFIXES } from "@/lib/dashboardTheme";

type Mode = "light" | "dark";

type ThemeContextValue = { mode: Mode; toggle: () => void; isThemed: boolean };
const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useDashboardTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useDashboardTheme must be used within DashboardThemeProvider");
  return ctx;
}

export function DashboardThemeProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isThemed = THEMED_ROUTE_PREFIXES.some((prefix) => pathname?.startsWith(prefix) ?? false);
  const [mode, setMode] = useState<Mode>("light");

  // Load the persisted preference once on mount (the init script already applied
  // it pre-paint; this syncs React state to it).
  useEffect(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark") setMode(stored);
  }, []);

  // Keep the class in sync across client-side navigation and toggles. Dark only
  // on the themed study routes (dashboard + textbook); everywhere else is light.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", isThemed && mode === "dark");
  }, [isThemed, mode]);

  const toggle = useCallback(() => {
    setMode((prev) => {
      const next: Mode = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {}
      return next;
    });
  }, []);

  return <ThemeContext.Provider value={{ mode, toggle, isThemed }}>{children}</ThemeContext.Provider>;
}

export function ThemeToggle({ className }: { className?: string }) {
  const { mode, toggle } = useDashboardTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const isDark = mounted && mode === "dark";

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={cn(
        "flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-muted-foreground/20 bg-muted/30 text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground",
        className,
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </button>
  );
}
